import { useState, useEffect } from "react";
import { apiClient } from "../api/client";
import Card from "../components/Card";

interface Criteria {
  id: string;
  name: string;
  description?: string;
  category: "PERFORMANCE" | "BEHAVIOR" | "SKILL" | "OTHER";
  weight: number;
  maxScore: number;
  displayOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface CriteriaFormData {
  name: string;
  description?: string;
  category: "PERFORMANCE" | "BEHAVIOR" | "SKILL" | "OTHER";
  weight: number;
  maxScore: number;
  displayOrder: number;
}

const categoryColors: Record<string, string> = {
  PERFORMANCE: "bg-blue-100 text-blue-700",
  BEHAVIOR: "bg-purple-100 text-purple-700",
  SKILL: "bg-green-100 text-green-700",
  OTHER: "bg-gray-100 text-gray-700",
};

function EvaluationCriteria() {
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<Criteria | null>(null);
  const [formData, setFormData] = useState<CriteriaFormData>({
    name: "",
    description: undefined,
    category: "PERFORMANCE",
    weight: 1.0,
    maxScore: 5,
    displayOrder: 0,
  });

  // Fetch criteria
  const fetchCriteria = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<{ criteria: Criteria[] }>("/evaluation-criteria");
      setCriteria(response.criteria || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load criteria");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCriteria();
  }, []);

  // Handle create criteria
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post("/evaluation-criteria", formData);
      setShowCreateForm(false);
      setFormData({
        name: "",
        description: undefined,
        category: "PERFORMANCE",
        weight: 1.0,
        maxScore: 5,
        displayOrder: 0,
      });
      await fetchCriteria();
    } catch (err: any) {
      alert(err.message || "Failed to create criteria");
    }
  };

  // Handle update criteria
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCriteria) return;

    try {
      const updates: any = {};
      if (formData.name !== editingCriteria.name) updates.name = formData.name;
      if (formData.description !== editingCriteria.description)
        updates.description = formData.description;
      if (formData.weight !== editingCriteria.weight) updates.weight = formData.weight;
      if (formData.maxScore !== editingCriteria.maxScore) updates.maxScore = formData.maxScore;
      if (formData.displayOrder !== editingCriteria.displayOrder)
        updates.displayOrder = formData.displayOrder;

      await apiClient.patch(`/evaluation-criteria/${editingCriteria.id}`, updates);
      setEditingCriteria(null);
      setFormData({
        name: "",
        description: undefined,
        category: "PERFORMANCE",
        weight: 1.0,
        maxScore: 5,
        displayOrder: 0,
      });
      await fetchCriteria();
    } catch (err: any) {
      alert(err.message || "Failed to update criteria");
    }
  };

  // Handle delete criteria
  const handleDelete = async (criteriaId: string) => {
    if (!confirm("Are you sure you want to delete this criteria?")) return;

    try {
      await apiClient.delete(`/evaluation-criteria/${criteriaId}`);
      await fetchCriteria();
    } catch (err: any) {
      alert(err.message || "Failed to delete criteria");
    }
  };

  // Start editing
  const startEdit = (c: Criteria) => {
    setEditingCriteria(c);
    setFormData({
      name: c.name,
      description: c.description,
      category: c.category,
      weight: c.weight,
      maxScore: c.maxScore,
      displayOrder: c.displayOrder,
    });
    setShowCreateForm(false);
  };

  // Cancel edit/create
  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingCriteria(null);
    setFormData({
      name: "",
      description: undefined,
      category: "PERFORMANCE",
      weight: 1.0,
      maxScore: 5,
      displayOrder: 0,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading evaluation criteria...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Evaluation Criteria</h2>
          <p className="text-sm text-slate-500 mt-1">
            Define assessment criteria for performance evaluations
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          + Add Criteria
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Create/Edit Form */}
      {(showCreateForm || editingCriteria) && (
        <Card title={editingCriteria ? "Edit Criteria" : "Create New Criteria"}>
          <form onSubmit={editingCriteria ? handleUpdate : handleCreate} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category: e.target.value as CriteriaFormData["category"],
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="PERFORMANCE">Performance</option>
                  <option value="BEHAVIOR">Behavior</option>
                  <option value="SKILL">Skill</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value || undefined })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows={2}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Weight</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Max Score</label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxScore}
                  onChange={(e) => setFormData({ ...formData, maxScore: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Order</label>
                <input
                  type="number"
                  min="0"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData({ ...formData, displayOrder: parseInt(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {editingCriteria ? "Update Criteria" : "Create Criteria"}
              </button>
              <button
                type="button"
                onClick={cancelForm}
                className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Criteria Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {criteria.length === 0 ? (
          <div className="col-span-2 text-center py-12 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-slate-500">No criteria defined. Create one to get started.</p>
          </div>
        ) : (
          criteria.map((c) => (
            <Card key={c.id} title={c.name} subtitle={c.description}>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${categoryColors[c.category]}`}
                  >
                    {c.category}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs uppercase">Weight</p>
                    <p className="font-semibold text-slate-900">{c.weight}x</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase">Max Score</p>
                    <p className="font-semibold text-slate-900">{c.maxScore}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase">Order</p>
                    <p className="font-semibold text-slate-900">{c.displayOrder}</p>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => startEdit(c)}
                    className="flex-1 px-3 py-1 text-sm bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-100 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(c.id)}
                    className="flex-1 px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

export default EvaluationCriteria;
